import { InjectionToken } from '@angular/core';

/**
 * Configuration interface for XMedical Core library
 * Apps must provide this configuration when initializing the library
 *
 * This interface only includes configuration needed by the core library itself.
 * App-specific concerns (OAuth, Pusher, Sentry, etc.) belong in app environment config.
 */
export interface XMedicalConfig {
  /**
   * Base URL for the main API
   * Example: 'https://api.xmedical.com'
   */
  apiUrl: string;

  /**
   * Base URL for the admin API
   * Example: 'https://admin-api.xmedical.com'
   */
  adminApiUrl: string;

  /**
   * Application version string
   * Used for API tracking via x-application-version header
   * Example: '1.0.0', '2.1.3-beta'
   */
  version: string;

  /**
   * Storage key names for browser storage
   * These keys are used by the authentication interceptor to track session state
   */
  storageKeys: {
    /**
     * Session storage key for access token
     */
    accessToken: string;

    /**
     * Local storage key for selected practice
     */
    selectedPractice: string;

    /**
     * Local storage key for installation ID
     */
    installationId: string;

    /**
     * Session storage key for session state
     */
    sessionId: string;

    /**
     * Session storage key for socket ID
     */
    socketId: string;
  };
}

/**
 * Injection token for XMedical configuration
 * Use this token to inject configuration in services
 *
 * @example
 * ```typescript
 * export class ApiService {
 *   private config = inject(XMEDICAL_CONFIG);
 *
 *   get(endpoint: string) {
 *     return this.http.get(`${this.config.apiUrl}${endpoint}`);
 *   }
 * }
 * ```
 */
export const XMEDICAL_CONFIG = new InjectionToken<XMedicalConfig>('xmedical.config');

/**
 * Convenience injection token for API base URL
 * Provided automatically by XMedicalCoreModule
 */
export const API_BASE_URL = new InjectionToken<string>('api.base.url');

/**
 * Convenience injection token for Admin API base URL
 * Provided automatically by XMedicalCoreModule
 */
export const ADMIN_API_BASE_URL = new InjectionToken<string>('admin.api.base.url');
